import React, { useState, useEffect } from "react";
import { getDatabase, ref, get, update, onValue } from "firebase/database";
import "./AdminDeliver.css";
import { createPortal } from "react-dom";

export default function AdminDeliver({ fgNumber, nodeName, onBack }) {
  const db = getDatabase();
  const [staffList, setStaffList] = useState([]);
  const [images, setImages] = useState({ qrUrl: "" });
  const [form, setForm] = useState({
    dateDeliver: "",
    timeDeliver: "",
    userName: "",
    quantity: "", // 🟢 Added quantity attribute
  });

  const [editRemark, setEditRemark] = useState("");
  const [zoomImage, setZoomImage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // NEW REMARK State
  const [issue, setIssue] = useState({
    message: "",
    reportedBy: "",
    reportedTime: "",
    status: "open"
  });

  useEffect(() => {
    if (zoomImage) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
  }, [zoomImage]);

  const androidToISODate = (val) => {
    if (!val) return "";
    const parts = val.split("/");
    if (parts.length !== 3) return "";
    const [d, m, y] = parts.map((s) => s.padStart(2, "0"));
    return `${y}-${m}-${d}`;
  };

  const isoToAndroidDate = (val) => {
    if (!val) return "";
    const [y, m, d] = val.split("-");
    return `${d}/${m}/${y}`;
  };

  const androidTimeTo24 = (val) => {
    if (!val) return "";
    const [time, modifier] = val.split(" ");
    if (!time) return val;
    let [h, mm] = time.split(":");
    let hour = parseInt(h, 10);
    if (modifier?.toLowerCase() === "pm" && hour < 12) hour += 12;
    if (modifier?.toLowerCase() === "am" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${mm}`;
  };

  const time24ToAndroid = (val) => {
    if (!val) return "";
    const [h, mm] = val.split(":");
    let hour = parseInt(h, 10);
    const modifier = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${mm} ${modifier}`;
  };

  useEffect(() => {
    if (!fgNumber || !nodeName) return;

    const nodeRef = ref(db, `${nodeName}/${fgNumber}`);
    const staffRef = ref(db, "Staff");

    // Helper utility to test if a string variable is blank, non-existent, or evaluated as "N/A"
    const isInvalidQuantity = (qty) => !qty || qty === "N/A";

    get(nodeRef)
      .then(async (snap) => {
        if (snap.exists()) {
          const data = snap.val();
          let cleanQuantity = data.quantity;

          // 🟢 OPTION A FALLBACK ENGINE START
          if (isInvalidQuantity(cleanQuantity)) {
            console.log(`[Quantity Fallback] Deliver info for ${fgNumber} lacks valid quantity. checking 'Pack'...`);
            
            const packSnap = await get(ref(db, `Pack/${fgNumber}`));
            if (packSnap.exists() && !isInvalidQuantity(packSnap.val().quantity)) {
              cleanQuantity = packSnap.val().quantity;
            } else {
              console.log(`[Quantity Fallback] 'Pack' missing or "N/A". checking 'Receive'...`);
              const receiveSnap = await get(ref(db, `Receive/${fgNumber}`));
              if (receiveSnap.exists() && !isInvalidQuantity(receiveSnap.val().quantity)) {
                cleanQuantity = receiveSnap.val().quantity;
              } else {
                console.log(`[Quantity Fallback] All fallback stations returned blank/"N/A". Initializing input field as empty.`);
                cleanQuantity = ""; 
              }
            }
          }
          // 🟢 OPTION A FALLBACK ENGINE END

          setForm({
            dateDeliver: data.dateDeliver ? androidToISODate(data.dateDeliver) : "",
            timeDeliver: data.timeDeliver ? androidTimeTo24(data.timeDeliver) : "",
            userName: data.userName || "",
            quantity: cleanQuantity, 
          });
          setImages({ qrUrl: data.qrImageUrl || "" });

        } else {
          const now = new Date();
          setForm({
            dateDeliver: now.toISOString().split("T")[0],
            timeDeliver: now.toTimeString().slice(0, 5),
            userName: "",
            quantity: "", 
          });
        }
      })
      .catch(console.error);

    get(staffRef)
      .then((snap) => {
        const staff = [];
        snap.forEach((child) => {
          const name = child.child("name").val();
          if (name) staff.push(name);
        });
        setStaffList(staff);
      })
      .catch(console.error);
  }, [fgNumber, nodeName, db]);

  const handleSave = async () => {
    if (!fgNumber || !nodeName) return;

    const rootRef = ref(db);
    const now = Date.now();
    
    // Construct all updates in one object for an atomic operation
    const updates = {};
    
    // 1. Core Deliver Fields
    if (form.dateDeliver) updates[`${nodeName}/${fgNumber}/dateDeliver`] = isoToAndroidDate(form.dateDeliver);
    if (form.timeDeliver) updates[`${nodeName}/${fgNumber}/timeDeliver`] = time24ToAndroid(form.timeDeliver);
    if (form.userName) updates[`${nodeName}/${fgNumber}/userName`] = form.userName;
    
    // 🟢 Save the updated quantity value back to the selected Node entry
    updates[`${nodeName}/${fgNumber}/quantity`] = form.quantity; 

    // 2. Sync to Progress Node
    updates[`Progress/${fgNumber}/s5`] = true;

    // 3. Close issue with remark if admin typed something
    if (editRemark.trim()) {
      const issuePath = `Deliver/${fgNumber}/issue`;
      updates[`${issuePath}/remark`] = editRemark.trim();
      updates[`${issuePath}/resolvedBy`] = form.userName || "Admin";
      updates[`${issuePath}/resolvedTime`] = now;
      updates[`${issuePath}/status`] = "closed";
      
      // Update local state so UI reflects closed issue immediately
      setIssue(prev => ({
        ...prev,
        remark: editRemark.trim(),
        resolvedBy: form.userName || "Admin",
        resolvedTime: now,
        status: "closed",
      }));
    }

    try {
      // Perform one atomic update across multiple nodes
      await update(rootRef, updates);

      // Trigger navigation back
      if (onBack) onBack();
    } catch (err) {
      document.body.style.overflow = "";
      console.error("Save failed:", err);
      alert(`Update failed: ${err.message}`);
    }
  };

  const handleDeleteFG = async () => {
    if (!fgNumber) {
      alert("FG Number not found.");
      return;
    }

    setIsDeleting(true); // 🌀 Show overlay

    const updates = {
      [`Deliver/${fgNumber}`]: null,
      [`Progress/${fgNumber}/s5`]: null,
      [`Receive/${fgNumber}/locationTag`]: "Pack"
    };

    try {
      const rootRef = ref(db);
      await update(rootRef, updates);

      console.log(`Delivery record for ${fgNumber} rolled back successfully.`);

      setTimeout(() => {
        if (onBack) onBack();
      }, 1000);

    } catch (err) {
      console.error("❌ Error during Deliver rollback:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setIsDeleting(false); // 🧹 Hide overlay
    }
  };

  // ------------------ Remark Realtime Sync ------------------
  useEffect(() => {
    if (!fgNumber) return;

    const issueRef = ref(db, `Deliver/${fgNumber}/issue`);
    const unsubscribe = onValue(issueRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setIssue(data);
        setEditRemark(data.remark || ""); // populate
      } else {
        setIssue({
          message: "",
          reportedBy: "",
          reportedTime: "",
          status: "open",
        });
        setEditRemark("");
      }
    });

    return () => unsubscribe();
  }, [fgNumber, db]); // Added 'db' to dependencies safely

  // ------------------ Convert Timestamp to Readable Time ------------------
  const formatTime = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="admin-deliver-root">
      <div className="card">
        <h2 className="title">{nodeName} for {fgNumber}</h2>
        
        {issue?.message && (
          <div className="issue-card">
            {/* Header */}
            <div className="issue-header">
              <div className="issue-title">⚠ Issue Reported</div>
              <span className={`issue-status ${issue.status}`}>
                {issue.status ? issue.status.toUpperCase() : "UNKNOWN"}
              </span>
            </div>

            {/* Issue Message */}
            <div className="issue-message">
              {issue.message || "No issue message"}
            </div>

            {/* Meta Info */}
            <div className="issue-meta">
              <span>👤 {issue.reportedBy || "-"}</span>
              <span>🕒 {formatTime(issue.reportedTime)}</span>
            </div>

            {/* Divider */}
            <div className="issue-divider"></div>

            {/* Resolved Info */}
            {issue?.status === "closed" && (
              <div className="issue-resolved-info">
                Resolved by: {issue?.resolvedBy || "-"} | {formatTime(issue?.resolvedTime)}
              </div>
            )}

            {/* Editable Remark */}
            <textarea
              className="remark-editor"
              value={editRemark}
              placeholder="Enter or edit admin remark"
              onChange={(e) => setEditRemark(e.target.value)}
            />
          </div>
        )}

        <button className="delete-fg-btn" onClick={handleDeleteFG} title="Delete FG">
          Delete Deliver 🗑
        </button>

        <div className="images-row">
          <div className="image-box" onClick={() => setZoomImage(images.qrUrl)}>
            <img src={images.qrUrl} alt="QR" />
            <div className="image-label">QR Code</div>
          </div>
        </div>

        {zoomImage && createPortal(
          <div className="image-zoom-overlay" onClick={() => setZoomImage(null)}>
            <div className="image-zoom-wrapper">
              <img src={zoomImage} alt="Zoomed" />
            </div>
          </div>,
          document.body
        )}

        <div className="form">
          <div className="date-time-row">
            <label className="label">Deliver Date:</label>
            <input
              type="date"
              value={form.dateDeliver}
              onChange={(e) => setForm({ ...form, dateDeliver: e.target.value })}
            />
          </div>

          <div className="date-time-row">
            <label className="label">Deliver Time:</label>
            <input
              type="time"
              value={form.timeDeliver}
              onChange={(e) => setForm({ ...form, timeDeliver: e.target.value })}
            />
          </div>

          <label className="form-row">
            <span className="label">User:</span>
            <select
              value={form.userName}
              onChange={(e) => setForm({ ...form, userName: e.target.value })}
            >
              <option value="">Select User</option>
              {staffList.map((s, idx) => (
                <option key={idx} value={s}>{s}</option>
              ))}
            </select>
          </label>

          <div className="actions">
            <button className="btn-back" onClick={() => onBack && onBack()}>⬅ Back</button>
            <button className="btn-save" onClick={handleSave}>💾 Save</button>
          </div>
        </div>
      </div>
      {isDeleting && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Deleting FG Number...</p>
        </div>
      )}
    </div>
  );
}